/**
 * Field Editor Component
 * Displays and allows editing of extracted/corrected fields
 */

import React, { useState, useCallback } from 'react';
import { getSchema, getFieldLabel, isFieldRequired, getFieldType, validateData } from '../../utils/fieldSchemas';
import { extractAddressComponents } from '../../utils/addressParser';
import HotelAutocomplete from '../../../dashboard/HotelAutocomplete';
import LegEditor from './LegEditor';
import LegNavigator from './LegNavigator';
import FareTermsFormSection from '../../../dashboard/FareTermsFormSection';

/**
 * Inline editable field component
 */
const InlineField = ({
  field,
  label,
  value,
  type = 'text',
  required = false,
  confidence,
  onChange,
  disabled = false
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [localValue, setLocalValue] = useState(value || '');

  const handleBlur = useCallback(() => {
    setIsEditing(false);
    if (localValue !== value) {
      onChange(field, localValue);
    }
  }, [field, localValue, value, onChange]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter' && type !== 'textarea') {
      e.preventDefault();
      handleBlur();
    }
    if (e.key === 'Escape') {
      setLocalValue(value || '');
      setIsEditing(false);
    }
  }, [handleBlur, value, type]);

  const getStatusIcon = () => {
    if (!value && required) {
      return <span className="text-red-500" title="Required field missing">✗</span>;
    }
    if (confidence !== undefined) {
      if (confidence >= 0.9) return <span className="text-green-500" title={`Confidence: ${Math.round(confidence * 100)}%`}>✓</span>;
      if (confidence >= 0.7) return <span className="text-yellow-500" title={`Confidence: ${Math.round(confidence * 100)}%`}>~</span>;
      return <span className="text-gray-400" title={`Confidence: ${Math.round(confidence * 100)}%`}>?</span>;
    }
    if (value) {
      return <span className="text-green-500">✓</span>;
    }
    return <span className="text-gray-300">○</span>;
  };

  const renderInput = () => {
    const baseClasses = "w-full px-2 py-1 text-sm border rounded focus:outline-none focus:ring-1 focus:ring-blue-500";

    switch (type) {
      case 'date':
        return (
          <input
            type="date"
            value={localValue?.slice(0, 10) || ''}
            onChange={(e) => setLocalValue(e.target.value)}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            disabled={disabled}
            className={baseClasses}
            autoFocus
          />
        );
      case 'time':
        return (
          <input
            type="time"
            value={localValue?.slice(0, 5) || ''}
            onChange={(e) => setLocalValue(e.target.value)}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            disabled={disabled}
            className={baseClasses}
            autoFocus
          />
        );
      case 'number':
        return (
          <input
            type="number"
            value={localValue || ''}
            onChange={(e) => setLocalValue(e.target.value)}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            disabled={disabled}
            className={baseClasses}
            autoFocus
          />
        );
      default:
        return (
          <input
            type="text"
            value={localValue || ''}
            onChange={(e) => setLocalValue(e.target.value)}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            disabled={disabled}
            className={baseClasses}
            autoFocus
          />
        );
    }
  };

  return (
    <div className="flex items-center gap-2 py-1.5 border-b border-gray-100 last:border-b-0">
      <div className="w-5 text-center flex-shrink-0">
        {getStatusIcon()}
      </div>
      <div className="w-28 flex-shrink-0 text-xs text-gray-500">
        {label}
        {required && <span className="text-red-400 ml-0.5">*</span>}
      </div>
      <div className="flex-1">
        {isEditing ? (
          renderInput()
        ) : (
          <div
            onClick={() => !disabled && setIsEditing(true)}
            className={`px-2 py-1 text-sm rounded cursor-pointer hover:bg-gray-50 ${
              value ? 'text-gray-900' : 'text-gray-400 italic'
            } ${disabled ? 'cursor-default' : ''}`}
          >
            {value || (required ? 'Click to enter' : 'Not set')}
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * Place Search Bar - auto-fills fields for hotel/restaurant types
 */
const PlaceSearchBar = ({ documentType, onChange, disabled }) => {
  const [searchValue, setSearchValue] = useState('');
  const [populated, setPopulated] = useState(null);

  const handlePlaceSelect = useCallback((placeDetails) => {
    const address = extractAddressComponents(placeDetails);
    let fieldsPopulated = 0;

    if (documentType === 'hotel') {
      if (placeDetails.name) { onChange('hotel_name', placeDetails.name); fieldsPopulated++; }
      if (placeDetails.formatted_address) { onChange('address', placeDetails.formatted_address); fieldsPopulated++; }
      if (address.city) { onChange('city', address.city); fieldsPopulated++; }
      if (address.country) { onChange('country', address.country); fieldsPopulated++; }
      if (placeDetails.phone) { onChange('phone', placeDetails.phone); fieldsPopulated++; }
    } else if (documentType === 'restaurant') {
      if (placeDetails.name) { onChange('restaurant_name', placeDetails.name); fieldsPopulated++; }
      if (placeDetails.formatted_address) { onChange('address', placeDetails.formatted_address); fieldsPopulated++; }
      if (placeDetails.phone) { onChange('phone', placeDetails.phone); fieldsPopulated++; }
    }

    setPopulated(fieldsPopulated);
    setTimeout(() => setPopulated(null), 3000);
    setSearchValue('');
  }, [documentType, onChange]);

  if (documentType !== 'hotel' && documentType !== 'restaurant') {
    return null;
  }

  const placeholder = documentType === 'hotel'
    ? 'Search hotel to auto-fill details...'
    : 'Search restaurant to auto-fill details...';

  return (
    <div className="mb-3 p-3 bg-indigo-50 border border-indigo-200 rounded-lg">
      <div className="flex items-center gap-2 mb-2">
        <svg className="w-4 h-4 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
        </svg>
        <span className="text-xs font-medium text-indigo-700">Quick Search</span>
        {populated !== null && (
          <span className="text-xs text-green-600 font-medium ml-auto flex items-center gap-1">
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            {populated} field{populated !== 1 ? 's' : ''} populated
          </span>
        )}
      </div>
      <HotelAutocomplete
        value={searchValue}
        onChange={setSearchValue}
        onPlaceSelect={handlePlaceSelect}
        placeholder={placeholder}
        className="w-full px-3 py-2 text-sm border border-indigo-300 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500 bg-white"
        disabled={disabled}
      />
    </div>
  );
};

/**
 * Main Field Editor Component
 */
const FieldEditor = ({
  documentType = 'flight',
  data = {},
  confidence = {},
  onChange,
  onLegChange,
  onAddLeg,
  onRemoveLeg,
  disabled = false,
  showValidation = true,
  className = ''
}) => {
  const schema = getSchema(documentType);
  const validation = showValidation ? validateData(documentType, data) : { isValid: true, errors: [], missingFields: [] };

  // Handle field change
  const handleFieldChange = useCallback((field, value) => {
    onChange?.(field, value);
  }, [onChange]);

  // Get top-level fields (exclude legs and internal fields)
  const topLevelFields = [...schema.required, ...schema.optional].filter(
    field => !field.startsWith('_') && field !== 'legs' && field !== 'passengers'
  );

  // Get legs data
  const legs = data.legs || [];
  const hasLegs = schema.multiLeg && legs.length > 0;

  return (
    <div className={`${className}`}>
      {/* Place search for hotel/restaurant */}
      <PlaceSearchBar
        documentType={documentType}
        onChange={handleFieldChange}
        disabled={disabled}
      />

      {/* Validation errors */}
      {showValidation && !validation.isValid && (
        <div className="mb-3 p-2 bg-red-50 border border-red-200 rounded-lg">
          <div className="text-xs font-medium text-red-800 mb-1">
            {validation.errors.length} issue{validation.errors.length !== 1 ? 's' : ''} found
          </div>
          <ul className="text-xs text-red-600 space-y-0.5">
            {validation.errors.slice(0, 3).map((error, idx) => (
              <li key={idx}>• {error}</li>
            ))}
            {validation.errors.length > 3 && (
              <li className="text-red-500">+ {validation.errors.length - 3} more</li>
            )}
          </ul>
        </div>
      )}

      {/* Top-level fields */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="px-3 py-2 bg-gray-50 border-b border-gray-200">
          <span className="text-xs font-medium text-gray-700">
            {schema.label} Details
          </span>
        </div>
        <div className="px-3 py-1">
          {topLevelFields.map(field => (
            <InlineField
              key={field}
              field={field}
              label={getFieldLabel(documentType, field)}
              value={data[field]}
              type={getFieldType(documentType, field)}
              required={isFieldRequired(documentType, field)}
              confidence={confidence[field]}
              onChange={handleFieldChange}
              disabled={disabled}
            />
          ))}
        </div>
      </div>

      {/* Legs section (for flights) - uses tab-based navigator */}
      {schema.multiLeg && (
        <div className="mt-3">
          <LegNavigator
            legs={legs}
            documentType={documentType}
            onLegChange={onLegChange}
            onAddLeg={onAddLeg}
            onRemoveLeg={onRemoveLeg}
            disabled={disabled}
          />
        </div>
      )}

      {/* Fare & Bags (flights) - only what the confirmation states */}
      {schema.fareTerms && (
        <div className="mt-3">
          <FareTermsFormSection
            value={data.fare_terms || null}
            onChange={(terms) => handleFieldChange('fare_terms', terms)}
            disabled={disabled}
          />
        </div>
      )}
    </div>
  );
};

export default FieldEditor;
